import {
  FocusableElement,
  MENU_AIM,
  Menu,
  MenuAim,
  PointerFocusTracker,
  Toggler,
} from '@angular/cdk/menu';
import { DOCUMENT } from '@angular/common';
import { Directive, Injectable, NgZone, OnDestroy, RendererFactory2, inject } from '@angular/core';
import { Subscription } from 'rxjs';

/**
 * How long a toggle stays deferred while the pointer is inside the safe
 * triangle. The pointer has this long to either reach the submenu or settle on
 * a sibling item (which then wins).
 */
const CLOSE_DELAY_MS = 500;

/**
 * The open submenu's bounding box is expanded by this much on every side, so
 * near-misses at the submenu's corners still count as aiming at it.
 */
const RECT_PADDING_PX = 24;

interface Point {
  x: number;
  y: number;
}

function pointInTriangle(p: Point, a: Point, b: Point, c: Point): boolean {
  const sign = (p1: Point, p2: Point, p3: Point) => {
    return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  };

  const d1 = sign(p, a, b);
  const d2 = sign(p, b, c);
  const d3 = sign(p, c, a);

  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;

  return !(hasNegative && hasPositive);
}

function pointInRect(p: Point, rect: DOMRect, padding = 0): boolean {
  return (
    p.x >= rect.left - padding &&
    p.x <= rect.right + padding &&
    p.y >= rect.top - padding &&
    p.y <= rect.bottom + padding
  );
}

/**
 * A replacement for the CDK's `TargetMenuAim`, provided for `MENU_AIM` by
 * `MdwMenuAimDirective`.
 *
 * The stock implementation is unusable with meadow menus for two reasons:
 *
 * - It reads the open submenu off `PointerFocusTracker.previousElement`, but
 *   the tracker listens to `mouseout` — which fires (and bubbles) on
 *   transitions into an item's own children. `MdwListItem` renders nested
 *   elements inside every `<li>`, so `activeElement` is cleared while the
 *   pointer is still inside the item and `previousElement` ends up undefined
 *   by the time a sibling is entered. The stock aim therefore never finds a
 *   submenu to aim at and every hover closes instantly.
 * - Its trajectory heuristic judges the pointer's recent move trail, which
 *   still contains the approach *into* the trigger item and regularly vetoes
 *   genuine aiming.
 *
 * This implementation instead:
 *
 * - Captures the **safe-triangle anchor and the open submenu's rect** the
 *   moment the pointer leaves the item owning the open submenu (the tracker's
 *   `exited` event still identifies that item reliably, because each item's
 *   listener reports its own bound item).
 * - Defers a toggle while the pointer is inside the triangle spanned by that
 *   anchor and the submenu's near edge (padded by {@link RECT_PADDING_PX}),
 *   or already inside the padded submenu itself.
 * - Applies **latest-toggle-wins**: a request arriving while one is deferred
 *   replaces it (the stock drops it, which strands the wrong submenu open).
 *   When the deferral expires, the pending toggle runs if the pointer is
 *   still inside the parent menu, and is dropped if the pointer has made it
 *   into the submenu (or left entirely).
 */
@Injectable()
export class MdwSafeTriangleMenuAim implements MenuAim, OnDestroy {
  private ngZone = inject(NgZone);
  private renderer = inject(RendererFactory2).createRenderer(null, null);
  private document = inject(DOCUMENT);

  private menu?: Menu;
  private cleanupMousemove?: () => void;
  private exitSubscription?: Subscription;

  /** Last known pointer position (tracked document-wide). */
  private lastPoint: Point | null = null;

  /** Where the pointer left the item that owns the currently open submenu. */
  private anchor: Point | null = null;

  /** The open submenu's bounds, captured at the same moment as the anchor. */
  private submenuRect: DOMRect | null = null;

  private pendingToggle: (() => void) | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  public ngOnDestroy(): void {
    this.cleanupMousemove?.();
    this.exitSubscription?.unsubscribe();
    this.clearPending();
  }

  public initialize(
    menu: Menu,
    pointerTracker: PointerFocusTracker<FocusableElement & Toggler>,
  ): void {
    this.menu = menu;
    this.subscribeToMouseMoves();

    // The moment the pointer leaves the item whose submenu is open is the
    // apex of the safe triangle; the submenu's bounds are captured at the
    // same time because the tracker's element bookkeeping cannot be trusted
    // afterwards (see class comment). Inner-child transitions fire this too,
    // which only refreshes the anchor to the pointer's latest position inside
    // the item — the closest, most accurate apex.
    this.exitSubscription?.unsubscribe();
    this.exitSubscription = pointerTracker.exited.subscribe(item => {
      const submenu = item.getMenu();
      if (submenu && this.lastPoint) {
        this.anchor = { ...this.lastPoint };
        this.submenuRect = submenu.nativeElement.getBoundingClientRect();
      }
    });
  }

  public toggle(doToggle: () => void): void {
    if (this.menu?.orientation === 'horizontal') {
      doToggle();
      return;
    }

    if (!this.submenuRect || !this.isAiming()) {
      this.runNow(doToggle);
      return;
    }

    this.pendingToggle = doToggle;
    this.restartTimer();
  }

  private runNow(doToggle: () => void): void {
    this.clearPending();
    this.anchor = null;
    this.submenuRect = null;
    doToggle();
  }

  private restartTimer(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      // Pointer still inside the parent menu: the user settled on an item, so
      // run whatever they asked for last. Pointer gone (into the submenu, or
      // off the menu entirely): drop the toggle.
      const menuRect = this.menu?.nativeElement.getBoundingClientRect();
      const settledOnParent =
        !!menuRect && !!this.lastPoint && pointInRect(this.lastPoint, menuRect);

      if (settledOnParent && this.pendingToggle) {
        const pending = this.pendingToggle;
        this.runNow(pending);
      } else {
        this.clearPending();
      }
    }, CLOSE_DELAY_MS);
  }

  private clearPending(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.pendingToggle = null;
  }

  private isAiming(): boolean {
    const rect = this.submenuRect;
    const current = this.lastPoint;
    if (!rect || !current) {
      return false;
    }

    // Already (nearly) inside the submenu.
    if (pointInRect(current, rect, RECT_PADDING_PX)) {
      return true;
    }

    const apex = this.anchor;
    if (!apex) {
      return false;
    }

    // Safe triangle: apex where the pointer left the trigger item, opposite
    // side on the submenu edge facing it.
    const left = rect.left - RECT_PADDING_PX;
    const right = rect.right + RECT_PADDING_PX;
    let nearEdgeX: number;
    if (apex.x < left) {
      nearEdgeX = left;
    } else if (apex.x > right) {
      nearEdgeX = right;
    } else {
      // The anchor sits horizontally within the submenu's span (overlapping
      // layouts); the inside-rect check above is the only meaningful test.
      return false;
    }

    return pointInTriangle(
      current,
      apex,
      { x: nearEdgeX, y: rect.top - RECT_PADDING_PX },
      { x: nearEdgeX, y: rect.bottom + RECT_PADDING_PX },
    );
  }

  private subscribeToMouseMoves(): void {
    this.cleanupMousemove?.();

    // Document-wide, so the position stays current after the pointer leaves
    // the parent menu (e.g. once it is inside the submenu overlay).
    this.cleanupMousemove = this.ngZone.runOutsideAngular(() => {
      return this.renderer.listen(this.document, 'mousemove', (event: MouseEvent) => {
        this.lastPoint = { x: event.clientX, y: event.clientY };
      });
    });
  }
}

/**
 * Applies the meadow safe-triangle menu aim to a menu. Must sit on the same
 * element as `cdkMenu` (the CDK resolves `MENU_AIM` with `self: true`):
 *
 * ```html
 * <menu cdkMenu mdwMenuAim>...</menu>
 * ```
 *
 * Use on any menu whose items open nested submenus, so a diagonal pointer
 * path from the trigger item into its submenu doesn't close the submenu.
 */
@Directive({
  selector: '[mdwMenuAim]',
  exportAs: 'mdwMenuAim',
  providers: [{ provide: MENU_AIM, useClass: MdwSafeTriangleMenuAim }],
})
export class MdwMenuAimDirective {}
